import { apiBlog } from "@/api/blog.api.ts";
import { apiFiles } from "@/api/file.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { BlogPostAttachments } from "@/pages/blog/components/BlogPostAttachments.tsx";
import { useBlogEditContext } from "@/types/blog.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import {
  Box,
  ButtonIcon,
  FileDropzone,
  FontAwesomeIcon,
  ProgressBar,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { AxiosProgressEvent } from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface PendingAttachment {
  id: string;
  file: File;
  progress: number;
  error?: string;
}

interface BlogPostEditAttachmentsProps {
  onUploadingChange: (isUploading: boolean) => void;
}

export function BlogPostEditAttachments({ onUploadingChange }: BlogPostEditAttachmentsProps) {
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const { renderFileSize } = useFormatter();
  const { post, setPost, registerTemporaryFile, unregisterTemporaryFile, isTemporaryFile } =
    useBlogEditContext();
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  const updateProgress = (uploadId: string, progress: number) => {
    setPendingAttachments((current) =>
      current.map((item) => (item.id === uploadId ? { ...item, progress } : item))
    );
  };

  const uploadAttachments = async (files: File[]) => {
    const uploads = files.map((file) => ({ file, uploadId: crypto.randomUUID() }));
    setPendingAttachments((current) => [
      ...current,
      ...uploads.map(({ file, uploadId }) => ({ id: uploadId, file, progress: 0 })),
    ]);
    onUploadingChange(true);

    try {
      for (const { file, uploadId } of uploads) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", ApiFilePathTypes.blog_attachment);
        formData.append("is_public", "false");
        formData.append("generate_public_link", "false");
        formData.append("save_original_name", "true");

        const onUploadProgress = ({ loaded, total }: AxiosProgressEvent) => {
          updateProgress(uploadId, total ? Math.round((loaded / total) * 100) : 0);
        };
        const { data, error } = await apiFiles.add(formData, onUploadProgress);
        if (data) {
          registerTemporaryFile(data.uuid);
          setPost((current) =>
            current ? { ...current, files: [...current.files, data] } : current
          );
          setPendingAttachments((current) => current.filter((item) => item.id !== uploadId));
        } else {
          setPendingAttachments((current) =>
            current.map((item) =>
              item.id === uploadId ? { ...item, error: t("blog:attachment_upload_error") } : item
            )
          );
          handleErrors(error);
        }
      }
    } finally {
      onUploadingChange(false);
    }
  };

  const removeAttachment = async (file: ApiFile) => {
    if (isTemporaryFile(file.uuid)) {
      const { error } = await apiBlog.deleteAttachment(file.uuid);
      if (error) {
        handleErrors(error);
        return;
      }
      unregisterTemporaryFile(file.uuid);
    }
    setPost((current) =>
      current
        ? { ...current, files: current.files.filter((item) => item.uuid !== file.uuid) }
        : current
    );
  };

  return (
    <Stack className="card-panel" gap="lg">
      <Title header={t("blog:attachments")} caption={t("blog:attachments_caption")} />
      <FileDropzone
        className="min-h-32"
        accept={{ "*/*": [] }}
        multiple
        onDrop={(files) => void uploadAttachments(files)}
        isLoading={pendingAttachments.some((file) => !file.error)}
        ctaActionText={t("common:add_files")}
        description={t("common:all_file_types_supported")}
        activeDescription={t("common:upload_file_drag_and_drop")}
        descriptionSize="sm"
      />
      {post?.files && (
        <BlogPostAttachments
          postId={post.id}
          files={post.files}
          showTitle={false}
          isFileOpenDisabled={(file) => isTemporaryFile(file.uuid)}
          onRemove={(file) => void removeAttachment(file)}
        />
      )}
      {pendingAttachments.map((pendingFile) => (
        <Box key={pendingFile.id} className="rounded-xs bg-dark/5 px-2 py-2">
          <Stack gap="xs">
            <Stack direction="horizontal" justify="between" items="center" gap="sm">
              <Stack className="min-w-0" gap="xs">
                <Text className="truncate">{pendingFile.file.name}</Text>
                <Text className="text-secondary">
                  {[pendingFile.file.type, renderFileSize(pendingFile.file.size)]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </Stack>
              {pendingFile.error && (
                <ButtonIcon
                  variant="plain"
                  size="sm"
                  title={t("blog:attachment_remove")}
                  onClick={() =>
                    setPendingAttachments((current) =>
                      current.filter((item) => item.id !== pendingFile.id)
                    )
                  }
                  icon={<FontAwesomeIcon icon="fa-light fa-times" />}
                />
              )}
            </Stack>
            {pendingFile.error ? (
              <Text className="text-danger">{pendingFile.error}</Text>
            ) : (
              <ProgressBar
                completed={pendingFile.progress}
                total={100}
                tooltip={`${pendingFile.progress}%`}
              />
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
